import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";

const Contact = () => {
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const [subject, setSubject] = useState("");

    useEffect(() => {
        const s = searchParams.get("subject");
        if (s) setSubject(s);
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        const body = {
            name: fd.get("name"),
            email: fd.get("email"),
            subject: fd.get("subject") || "",
            message: fd.get("message") || "",
        };
        if (!body.name || !body.email || !body.message) {
            toast.error("Please share your name, email and message.");
            return;
        }
        try {
            setLoading(true);
            await api.post("/contact", body);
            toast.success("Message sent! We'll get back to you within 1 business day.");
            e.target.reset();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <section className="bg-brand-navy text-white pt-20 pb-20">
                <div className="container-page max-w-4xl">
                    <p className="overline !text-brand-green">Contact</p>
                    <h1 className="mt-3 font-heading font-extrabold text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                        Let's talk. <span className="text-brand-green">Real humans answer here.</span>
                    </h1>
                    <p className="mt-6 text-white/80 text-lg max-w-2xl">
                        Whether you're hiring, job hunting, or partnering with us on community programs — we'd love to hear from you.
                    </p>
                </div>
            </section>

            <section className="section-pad bg-white">
                <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Info */}
                    <div className="lg:col-span-5">
                        <h2 className="font-heading font-bold text-2xl text-brand-navy">Get in touch</h2>
                        <p className="mt-2 text-brand-muted">We respond within 1 business day. Spanish welcome.</p>

                        <div className="mt-8 space-y-5">
                            {[
                                { icon: Mail, label: "Email", value: "hello@simonaratalent.us", href: "mailto:hello@simonaratalent.us" },
                                { icon: Phone, label: "Phone", value: "323-372-0646", href: "tel:+13233720646" },
                                { icon: MapPin, label: "Office", value: "Los Angeles, CA — Address coming soon", href: null },
                            ].map((row) => (
                                <div key={row.label} className="flex gap-4 items-start">
                                    <div className="w-11 h-11 rounded-xl bg-brand-softGreen text-brand-green flex items-center justify-center shrink-0">
                                        <row.icon size={18} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-brand-muted">{row.label}</div>
                                        {row.href ? (
                                            <a
                                                href={row.href}
                                                data-testid={`contact-info-${row.label.toLowerCase()}`}
                                                className="text-brand-navy font-semibold hover:text-brand-green transition-colors"
                                            >
                                                {row.value}
                                            </a>
                                        ) : (
                                            <div data-testid={`contact-info-${row.label.toLowerCase()}`} className="text-brand-navy font-semibold">{row.value}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10">
                            <div className="text-sm font-semibold text-brand-navy mb-3">Follow us</div>
                            <div className="flex items-center gap-3">
                                <a href="#" aria-label="LinkedIn" className="w-10 h-10 rounded-full bg-brand-navy text-white hover:bg-brand-green flex items-center justify-center transition"><Linkedin size={16} /></a>
                                <a href="#" aria-label="Facebook" className="w-10 h-10 rounded-full bg-brand-navy text-white hover:bg-brand-green flex items-center justify-center transition"><Facebook size={16} /></a>
                                <a href="#" aria-label="Instagram" className="w-10 h-10 rounded-full bg-brand-navy text-white hover:bg-brand-green flex items-center justify-center transition"><Instagram size={16} /></a>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={submit} className="lg:col-span-7 card-soft p-8 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <Label htmlFor="c-name">Name *</Label>
                                <Input id="c-name" name="name" data-testid={TID.contact.name} required className="mt-2" />
                            </div>
                            <div>
                                <Label htmlFor="c-email">Email *</Label>
                                <Input id="c-email" name="email" type="email" data-testid={TID.contact.email} required className="mt-2" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="c-subject">Subject</Label>
                            <Input
                                id="c-subject"
                                name="subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                data-testid={TID.contact.subject}
                                className="mt-2"
                            />
                        </div>
                        <div>
                            <Label htmlFor="c-message">Message *</Label>
                            <Textarea id="c-message" name="message" data-testid={TID.contact.message} rows={6} required className="mt-2" />
                        </div>
                        <button type="submit" disabled={loading} data-testid={TID.contact.submit} className="btn-navy disabled:opacity-60">
                            {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Sending...</> : <><Send size={18} className="mr-2" /> Send message</>}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Contact;
